import React, { useCallback, useEffect, useState } from "react";
import { API, authHeaders, resolveStorageUrl } from "../services/config";
import EmptyState from "./ui/EmptyState";
import "./RXPaciente.css";

const SITIOS_CREDENCIAL = [
  { value: "oulton", label: "Oulton" },
  { value: "maxity", label: "Maxity" },
  { value: "otro", label: "Otro (especificar cuál)" },
];

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "";
  const fecha = new Date(fechaStr + "T00:00:00");
  return fecha.toLocaleDateString("es-AR", { year: "numeric", month: "long", day: "numeric" });
};

const hoyISO = () => new Date().toISOString().split("T")[0];

export default function RXPaciente({ pacienteId, pacienteDni = "", embedded = false, onSaved }) {
  const [radiografias, setRadiografias] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedRX, setExpandedRX] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [form, setForm] = useState({
    nombre: "",
    fecha: hoyISO(),
    tipo_adjunto: "archivo",
    archivo: null,
    credencial_sitio: "oulton",
    credencial_sitio_otro: "",
    credencial_numero_estudio: "",
  });

  const fetchRadiografias = useCallback(async () => {
    const res = await fetch(`${API}/pacientes/${pacienteId}/radiografias`, { headers: authHeaders() });
    const json = await res.json();
    if (res.ok && json.success) {
      setRadiografias(
        (json.data || []).map((rx) => ({
          ...rx,
          archivo_url: resolveStorageUrl(rx.archivo_url),
        }))
      );
    }
  }, [pacienteId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await fetchRadiografias();
      } catch {
        setError("Error al cargar radiografías.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchRadiografias]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    document.body.classList.add("rx-modal-open");
    return () => document.body.classList.remove("rx-modal-open");
  }, [modalOpen]);

  const openModal = () => {
    setForm({
      nombre: "",
      fecha: hoyISO(),
      tipo_adjunto: "archivo",
      archivo: null,
      credencial_sitio: "oulton",
      credencial_sitio_otro: "",
      credencial_numero_estudio: "",
    });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleGuardar = async () => {
    if (!form.nombre.trim()) {
      setError("El nombre descriptivo es obligatorio.");
      return;
    }
    if (form.tipo_adjunto === "archivo" && !form.archivo) {
      setError("Debe adjuntar una imagen (jpg, png) o PDF.");
      return;
    }
    if (form.tipo_adjunto === "credencial") {
      if (!form.credencial_numero_estudio.trim()) {
        setError("El número de estudio es obligatorio.");
        return;
      }
      const sitio =
        form.credencial_sitio === "otro"
          ? form.credencial_sitio_otro.trim()
          : form.credencial_sitio;
      if (!sitio) {
        setError(
          form.credencial_sitio === "otro"
            ? "Indicá cuál es la clínica."
            : "Indicá el sitio de la clínica."
        );
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const body = new FormData();
      body.append("nombre", form.nombre.trim());
      body.append("fecha", form.fecha);
      body.append("tipo_adjunto", form.tipo_adjunto);

      if (form.tipo_adjunto === "archivo" && form.archivo) {
        body.append("archivo", form.archivo);
      } else {
        const sitio =
          form.credencial_sitio === "otro"
            ? form.credencial_sitio_otro.trim()
            : SITIOS_CREDENCIAL.find((s) => s.value === form.credencial_sitio)?.label || form.credencial_sitio;
        body.append("credencial_sitio", sitio);
        body.append("credencial_numero_estudio", form.credencial_numero_estudio.trim());
      }

      const res = await fetch(`${API}/pacientes/${pacienteId}/radiografias`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || "Error al guardar la radiografía.");
        return;
      }
      setSuccess("Radiografía guardada.");
      setTimeout(() => setSuccess(null), 3000);
      closeModal();
      await fetchRadiografias();
      if (onSaved) onSaved();
    } catch {
      setError("Error de conexión al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const copiarAlPortapapeles = async (texto, feedbackKey) => {
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(String(texto));
      setCopyFeedback(feedbackKey);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch {
      setError("No se pudo copiar al portapapeles.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta radiografía?")) return;
    const res = await fetch(`${API}/radiografias/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      setRadiografias((prev) => prev.filter((rx) => rx.id !== id));
      if (onSaved) onSaved();
    }
  };

  const renderRadiografia = (rx) => {
    const isExpanded = expandedRX === rx.id;
    const sitioLabel = rx.credencial_sitio || "";

    return (
      <div key={rx.id} className="rx-card">
        <button
          type="button"
          className="rx-card-header"
          onClick={() => setExpandedRX(isExpanded ? null : rx.id)}
        >
          <div className="rx-card-title">
            <h4>{rx.nombre}</h4>
            <p className="rx-tipos-preview">{formatFecha(rx.fecha)}</p>
          </div>
          <span className={`rx-expand-icon ${isExpanded ? "expanded" : ""}`}>▼</span>
        </button>

        {isExpanded && (
          <div className="rx-card-content">
            {rx.tipo_adjunto === "archivo" && rx.archivo_url && (
              <div className="rx-content-section">
                <strong>Archivo adjunto:</strong>
                <div className="rx-files-gallery">
                  {rx.archivo_nombre_original?.match(/\.(jpg|jpeg|png)$/i) ? (
                    <a href={rx.archivo_url} target="_blank" rel="noreferrer">
                      <img src={rx.archivo_url} alt={rx.archivo_nombre_original} className="rx-image-preview" />
                    </a>
                  ) : (
                    <a href={rx.archivo_url} target="_blank" rel="noreferrer" className="rx-file-download">
                      📄 {rx.archivo_nombre_original || "Ver archivo"}
                    </a>
                  )}
                </div>
              </div>
            )}

            {rx.tipo_adjunto === "credencial" && (
              <div className="rx-content-section">
                <strong>Visualización en clínica externa</strong>
                <p>Sitio: {sitioLabel}</p>

                <div className="rx-credencial-row">
                  <span className="rx-credencial-valor">
                    Nº de estudio: <strong>{rx.credencial_numero_estudio}</strong>
                  </span>
                  <button
                    type="button"
                    className="rx-btn-copiar boton-secundario"
                    onClick={() =>
                      copiarAlPortapapeles(rx.credencial_numero_estudio, `${rx.id}-estudio`)
                    }
                  >
                    {copyFeedback === `${rx.id}-estudio` ? "Copiado" : "Copiar nº estudio"}
                  </button>
                </div>

                {pacienteDni ? (
                  <div className="rx-credencial-row">
                    <span className="rx-credencial-valor">
                      DNI: <strong>{pacienteDni}</strong>
                    </span>
                    <button
                      type="button"
                      className="rx-btn-copiar boton-secundario"
                      onClick={() => copiarAlPortapapeles(pacienteDni, `${rx.id}-dni`)}
                    >
                      {copyFeedback === `${rx.id}-dni` ? "Copiado" : "Copiar DNI"}
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            <div className="rx-card-actions">
              <button type="button" className="rx-btn-delete" onClick={() => handleEliminar(rx.id)}>
                Eliminar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`rx-container ${embedded ? "embedded" : ""}`}>
      <div className="rx-header">
        <h3>Radiografías</h3>
        <p>Estudios realizados y acceso a imágenes del paciente</p>
      </div>

      {error && !modalOpen && <div className="rx-error">{error}</div>}
      {success && <div className="rx-success">{success}</div>}

      <button type="button" onClick={openModal} className="rx-btn-new pp-btn-agregar">
        + Nueva radiografía
      </button>

      {loading ? (
        <p className="rx-empty">Cargando...</p>
      ) : radiografias.length === 0 ? (
        <EmptyState title="Sin radiografías" description="No hay estudios cargados para este paciente." />
      ) : (
        <div className="rx-list">{radiografias.map(renderRadiografia)}</div>
      )}

      {modalOpen && (
        <div className="rx-modal-overlay" onClick={closeModal}>
          <div className="rx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rx-modal-content">
              <h3>Nueva radiografía</h3>

              <div className="rx-form-group">
                <label>Nombre descriptivo *</label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                  className="rx-input"
                  placeholder="Ej: Panorámica control, Periapical 36"
                />
              </div>

              <div className="rx-form-group">
                <label>Fecha</label>
                <input
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
                  className="rx-input"
                />
              </div>

              <div className="rx-form-group">
                <label>Tipo de registro</label>
                <div className="rx-tipo-toggle">
                  <button
                    type="button"
                    className={form.tipo_adjunto === "archivo" ? "active" : ""}
                    onClick={() => setForm((p) => ({ ...p, tipo_adjunto: "archivo" }))}
                  >
                    Archivo adjunto
                  </button>
                  <button
                    type="button"
                    className={form.tipo_adjunto === "credencial" ? "active" : ""}
                    onClick={() => setForm((p) => ({ ...p, tipo_adjunto: "credencial" }))}
                  >
                    Credenciales clínica
                  </button>
                </div>
              </div>

              {form.tipo_adjunto === "archivo" ? (
                <div className="rx-form-group">
                  <label htmlFor="rx-archivo-input">Adjunto (jpg, png o pdf) *</label>
                  <div className="rx-file-upload">
                    <input
                      type="file"
                      id="rx-archivo-input"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      className="rx-file-input"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, archivo: e.target.files?.[0] || null }))
                      }
                    />
                    <label htmlFor="rx-archivo-input" className="rx-file-label">
                      {form.archivo
                        ? `Archivo seleccionado: ${form.archivo.name}`
                        : "Seleccionar archivo"}
                    </label>
                  </div>
                </div>
              ) : (
                <>
                  <div className="rx-form-group">
                    <label>Sitio / clínica</label>
                    <select
                      className="rx-input"
                      value={form.credencial_sitio}
                      onChange={(e) => setForm((p) => ({ ...p, credencial_sitio: e.target.value }))}
                    >
                      {SITIOS_CREDENCIAL.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.credencial_sitio === "otro" && (
                    <div className="rx-form-group">
                      <label htmlFor="rx-sitio-otro">¿Cuál clínica? *</label>
                      <input
                        id="rx-sitio-otro"
                        type="text"
                        className="rx-input"
                        value={form.credencial_sitio_otro}
                        onChange={(e) => setForm((p) => ({ ...p, credencial_sitio_otro: e.target.value }))}
                        placeholder="Ej: Clínica Dental Norte, Rayos X Sur..."
                      />
                    </div>
                  )}
                  <div className="rx-form-group">
                    <label>Número de estudio *</label>
                    <input
                      type="text"
                      className="rx-input"
                      value={form.credencial_numero_estudio}
                      onChange={(e) => setForm((p) => ({ ...p, credencial_numero_estudio: e.target.value }))}
                    />
                  </div>
                  {pacienteDni && (
                    <p className="rx-credencial-dni">DNI del paciente (ya registrado): <strong>{pacienteDni}</strong></p>
                  )}
                </>
              )}

              {error && modalOpen && <div className="rx-error">{error}</div>}

              <div className="rx-modal-buttons modal-historia-buttons">
                <button type="button" className="btn-cerrar" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="button" className="btn-guardar" onClick={handleGuardar} disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
